import { SubscriptionPlan } from "@/networking/carts.api";
import React from "react";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";

export default function SubscriptionPlansDropdown({
  subscriptionPlans,
  selectedSubscriptionPlanId,
  onPlanSelect,
  disabled = false,
}: {
  subscriptionPlans: SubscriptionPlan[] | null;
  selectedSubscriptionPlanId?: number;
  onPlanSelect: (planId: number) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  if (!subscriptionPlans || subscriptionPlans.length === 0) {
    return null;
  }

  // Format the display text for a subscription plan
  const formatPlanDisplay = (plan: SubscriptionPlan) => {
    let frequencyText: string;
    // API returns a bare unit (e.g. "month", "3 months"), not a full phrase — we prepend t("every") so no "every every X" duplication.
    if (plan.billing_frequency_in_words) {
      frequencyText = `${t("every")} ${plan.billing_frequency_in_words}`;
    } else {
      const interval = plan.billing_interval;
      const unit = plan.billing_interval_unit;
      frequencyText =
        interval === 1
          ? `${t("every")} ${unit}`
          : `${t("every")} ${interval} ${unit}`;
    }

    let text = frequencyText;
    // Prefer the server-formatted savings_label ("Save 10%" / "Save $5.00")
    // which already includes the "Save" prefix and respects the plan's
    // savings_display_mode. Falls back to the legacy currency-only field
    // when the backend hasn't shipped savings_label yet.
    if (plan.savings_label) {
      text += `, ${plan.savings_label}`;
    } else if (
      plan.subscribe_and_save_in_currency &&
      plan.subscribe_and_save_in_currency !== "$0.00"
    ) {
      text += `, ${t("save")} ${plan.subscribe_and_save_in_currency}`;
    }
    return text;
  };

  // Default to first plan if none selected
  const currentPlan =
    subscriptionPlans.find((plan) => plan.id === selectedSubscriptionPlanId) ||
    subscriptionPlans[0];

  if (!currentPlan) {
    return null;
  }

  return (
    <div className="w-full">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <button
            className={`flex items-center justify-between rounded px-3 py-2 text-left text-xs ${
              disabled
                ? "cursor-not-allowed bg-gray-100 text-gray-400"
                : "bg-gray-100 text-gray-900"
            }`}
          >
            <span>{formatPlanDisplay(currentPlan)}</span>
            <ChevronDown
              className={`h-4 w-4 ${
                disabled ? "text-gray-400" : "text-gray-500"
              }`}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="z-50 max-w-[200px]"
          side="bottom"
          align="start"
          sideOffset={4}
        >
          {subscriptionPlans.map((plan) => (
            <DropdownMenuItem
              key={plan.id}
              className={`cursor-pointer px-3 py-2 text-xs ${
                plan.id === currentPlan.id
                  ? "bg-black font-medium text-white"
                  : "text-gray-900 hover:bg-gray-50"
              }`}
              onClick={() => onPlanSelect(plan.id)}
            >
              {formatPlanDisplay(plan)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
